import React, { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import { Toaster } from '../ui/sonner';
import PWAInstallBanner, { OfflineIndicator } from '../PWAInstallBanner';
import ProfileCompletionModal from '../ProfileCompletionModal';
import ChangePasswordModal from '../ChangePasswordModal';
import api from '../../lib/api';

const AppShell = () => {
  const { isAuthenticated, loading, user, requiresPasswordChange, clearPasswordChangeRequirement } = useAuth();
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [profileChecked, setProfileChecked] = useState(false);
  const [showForcedPasswordChange, setShowForcedPasswordChange] = useState(false);

  // Check if forced password change is required
  useEffect(() => {
    if (isAuthenticated && requiresPasswordChange) {
      setShowForcedPasswordChange(true);
    }
  }, [isAuthenticated, requiresPasswordChange]);

  // Check profile completion status for employees
  useEffect(() => {
    const checkProfileCompletion = async () => {
      // Only check for employee role
      if (!isAuthenticated || !user || user.role !== 'employee') {
        setProfileChecked(true);
        return;
      }
      
      // Check if user has skipped recently (within last 24 hours)
      const skippedTime = localStorage.getItem('profile_completion_skipped');
      if (skippedTime && (Date.now() - parseInt(skippedTime)) < 24 * 60 * 60 * 1000) {
        setProfileChecked(true);
        return;
      }
      
      try {
        const res = await api.get('/auth/profile-status');
        if (res.data.has_profile && !res.data.is_complete) {
          setShowProfileCompletion(true);
        }
      } catch (error) {
        console.error('Failed to check profile status');
      } finally {
        setProfileChecked(true);
      }
    };
    
    if (isAuthenticated && !profileChecked) {
      checkProfileCompletion();
    }
  }, [isAuthenticated, user, profileChecked]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="spinner w-8 h-8"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <OfflineIndicator />
      <Sidebar />
      <main className="ml-64 min-h-screen">
        <Outlet />
      </main>
      <Toaster position="top-right" richColors />
      <PWAInstallBanner />
      {/* Profile Completion Modal for employees with incomplete data */}
      <ProfileCompletionModal 
        isOpen={showProfileCompletion}
        onClose={() => setShowProfileCompletion(false)}
        onComplete={() => {
          setShowProfileCompletion(false);
          localStorage.removeItem('profile_completion_skipped');
        }}
      />
      {/* Forced Password Change Modal for first-time login */}
      <ChangePasswordModal
        open={showForcedPasswordChange}
        onOpenChange={() => {}} // Cannot close forced modal
        isForced={true}
        onSuccess={() => {
          setShowForcedPasswordChange(false);
          clearPasswordChangeRequirement();
        }}
      />
    </div>
  );
};

export default AppShell;
